import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { debugValue } from "@/env/utils";
import { Capacitor } from "@/lib/capacitor";
import { createSelectors } from "@/lib/store/utils/createSelectors";
import { type SelectedCoreValueDto } from "../backend/api";

export type ModalKeys =
	| "appCropProfilePicture"
	| "appDeleteAccount"
	| "appExplainer"
	| "appPushNotificationsDenied"
	| "coreValuesCompileExplainer"
	| "coreValuesCompileGeneral"
	| "coreValuesExplainer"
	| "coreValuesAccuracyExplainer"
	| "coreValuesAccuracyDetailsExplainer"
	| "coreValuesDetail"
	| "coreValuesPersonalizeCompleted"
	| "coreValuesPersonalizeExplainer"
	| "coreValuesResummarize"
	| "coreValuesFinished"
	| "devDeleteFriendcheck"
	| "friendCheckAnalysisGenerate"
	| "friendCheckAnalysisView"
	| "friendCheckAnalysisConfirm"
	| "friendCheckCompleted"
	| "friendCheckExplainer"
	| "integrationsImpolitic"
	| "reflectionAnalysisGenerate"
	| "reflectionAnalysisView"
	| "reflectionCompleted"
	| "reflectionExplainerGeneral"
	| "reflectionQuestionCompleted"
	| "reflectionQuestionFirstAnswer"
	| "perfectDayExplainer"
	| "perfectDayUpdate"
	| "perfectDayConfirmDelete";

type BaseModalState = { isOpen: boolean };

interface ModalStates extends Record<ModalKeys, BaseModalState> {
	appExplainer: BaseModalState & { onCompleted: VoidFunction | null };
	coreValuesDetail: BaseModalState & {
		item: {
			name?: string | null;
			reason?: string | null;
			score?: number | null;
			type?: "friend-check" | "reflection" | null;
		} | null;
	};
	coreValuesCompileExplainer: BaseModalState & { onCompleted: VoidFunction | null };
	coreValuesAccuracyDetailsExplainer: BaseModalState & { onCompleted: VoidFunction | null };
	coreValuesPersonalizeCompleted: BaseModalState & { id: string | null };
	reflectionExplainerGeneral: BaseModalState & { onCompleted: VoidFunction | null };
	reflectionQuestionCompleted: BaseModalState & {
		newSelectedCoreValues: SelectedCoreValueDto[] | null;
		updatedSelectedCoreValues: SelectedCoreValueDto[] | null;
	};
	reflectionQuestionFirstAnswer: BaseModalState & { id: string | null };
	appCropProfilePicture: BaseModalState & {
		imageUrl: string;
		onCompleted: ((crop: string) => void) | null;
	};
	perfectDayExplainer: BaseModalState & { onCompleted: VoidFunction | null };

	perfectDayUpdate: BaseModalState & {
		onCompleted: VoidFunction | null;
		id: string | null;
		name: string | null;
	};
	perfectDayConfirmDelete: BaseModalState & { onConfirmed: VoidFunction | null };
	friendCheckAnalysisConfirm: BaseModalState & {
		newCoreValues: { name: string; emoji: string }[] | null;
		onConfirmed: VoidFunction | null;
	};
}

type ModalActions = Record<
	"present" | "dismiss",
	<T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) => void
>;

export const useModalStore = create<ModalStates & ModalActions>()(
	devtools(
		immer((set) => ({
			appCropProfilePicture: { isOpen: false, imageUrl: "", onCompleted: null },
			appDeleteAccount: { isOpen: false },
			appExplainer: { isOpen: false, onCompleted: null },
			appPushNotificationsDenied: { isOpen: false },
			coreValuesCompileExplainer: { isOpen: false, onCompleted: null },
			coreValuesCompileGeneral: { isOpen: false },
			coreValuesDetail: { isOpen: false, item: null },
			coreValuesExplainer: { isOpen: false },
			coreValuesAccuracyExplainer: { isOpen: false },
			coreValuesAccuracyDetailsExplainer: { isOpen: false, onCompleted: null },
			coreValuesPersonalizeCompleted: { isOpen: false, id: null },
			coreValuesPersonalizeExplainer: { isOpen: false },
			coreValuesResummarize: { isOpen: false },
			coreValuesFinished: { isOpen: false },
			devDeleteFriendcheck: { isOpen: false },
			friendCheckAnalysisGenerate: { isOpen: false },
			friendCheckAnalysisView: { isOpen: false },
			friendCheckAnalysisConfirm: { isOpen: false, newCoreValues: null, onConfirmed: null },
			friendCheckCompleted: { isOpen: false },
			friendCheckExplainer: { isOpen: false },
			integrationsImpolitic: { isOpen: false },
			reflectionAnalysisGenerate: { isOpen: false },
			reflectionAnalysisView: { isOpen: false },
			reflectionCompleted: { isOpen: false },
			reflectionExplainerGeneral: { isOpen: false, onCompleted: null },
			reflectionQuestionCompleted: {
				isOpen: false,
				newSelectedCoreValues: null,
				updatedSelectedCoreValues: null,
			},
			reflectionQuestionFirstAnswer: { isOpen: false, id: null },
			perfectDayExplainer: { isOpen: false, onCompleted: null },
			perfectDayUpdate: { isOpen: false, id: null, name: null, onCompleted: null },
			perfectDayConfirmDelete: { isOpen: false, onConfirmed: null },

			present: <T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) =>
				set((state) => {
					state[key].isOpen = true;

					if (typeof update === "object") {
						state[key] = { ...state[key], ...update };
					}
				}),
			dismiss: <T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) =>
				set((state) => {
					state[key].isOpen = false;

					if (typeof update === "object") {
						state[key] = { ...state[key], ...update };
					}
				}),
		})),
		{
			enabled: debugValue("store") && Capacitor.Platform === "web",
		},
	),
);

export const useSelectorModalStore = createSelectors(useModalStore);
